import { useSettings } from './store/settings';
import { hub } from './devices/hub';
import { registerRideHooks, setVsFactory } from './ride/launch';
import { VirtualShifter } from './trainer/virtualShifting';
import { ZwiftClick } from './devices/click/ZwiftClick';
import { startControllerInput } from './ride/input';
import { getSettings } from './store/settings';
import { useAccount } from './store/account';
import { startBackgroundSync, syncNow } from './sync/sync';
import { uploadRideToStrava } from './sync/strava';
import { onRideSaved } from './ride/manager';
import { SIM_MODE } from './env';
import { getSimTrainer } from './devices/sim/simDevices';
import { getSession } from './ride/manager';

let started = false;
/** The Zwift Click module is experimental and only active behind its settings flag (SPEC §5.1). */
function applyClickFlag(): void {
  const s = getSettings();
  if (s.clickEnabled)
    hub.setClickFactory((device) => new ZwiftClick(device, { encrypted: s.clickEncryptedHandshake }));
  else {
    hub.setClickFactory(null);
    if (hub.click) void hub.detach('click', false);
  }
}

/** One-time app startup: load local state, reconnect remembered devices. */
export async function bootstrap(): Promise<void> {
  if (started) return;
  started = true;
  registerRideHooks();
  await useSettings.getState().load();
  setVsFactory(() => new VirtualShifter());
  applyClickFlag();
  useSettings.subscribe((st, prev) => {
    if (
      st.settings.clickEnabled !== prev.settings.clickEnabled ||
      st.settings.clickEncryptedHandshake !== prev.settings.clickEncryptedHandshake
    )
      applyClickFlag();
  });
  startControllerInput();
  // After each saved ride: sync, and upload to Strava when the policy is "auto" (default "ask").
  onRideSaved((ride) => {
    setTimeout(() => {
      void (async () => {
        await syncNow();
        if (useAccount.getState().status === 'loggedIn' && getSettings().uploadPolicy === 'auto') {
          await uploadRideToStrava(ride);
          await syncNow();
        }
      })();
    }, 500);
  });
  void useAccount
    .getState()
    .refresh()
    .then(() => {
      if (useAccount.getState().status === 'loggedIn') startBackgroundSync();
    });
  if (SIM_MODE) {
    // Test/debug hook: jump to a course distance (m) — screenshots and E2E of long routes.
    (window as unknown as { __velostubeTeleport: unknown }).__velostubeTeleport = (d: number) => {
      const s = getSession();
      if (!s) return false;
      s.courseD = d;
      s.sample = s.course.sample(d);
      return true;
    };
    // Test/debug hook: inspect what the simulated trainer received.
    (window as unknown as { __velostubeSim: unknown }).__velostubeSim = () => {
      const t = getSimTrainer();
      return {
        mode: t.mode,
        sim: t.sim,
        ergTarget: t.ergTarget,
        commands: t.commands.length,
        power: t.power,
        cadence: t.cadence,
      };
    };
  }
  void hub.autoReconnectAll();
}
